import { useRef, useState } from 'react';
import { FaCloudUploadAlt, FaTimes } from 'react-icons/fa';

/**
 * Drag-and-drop image upload — compact (horizontal) or default zone.
 */
const ImageDropzone = ({
  file,
  onFileChange,
  accept = 'image/png,image/jpeg,image/jpg',
  validateFile,
  disabled = false,
  previewUrl,
  hint = 'PNG atau JPG, maks. 1MB',
  emptyTitle = 'Drop file di sini, atau',
  pickLabel = 'pilih file',
  replaceHint = 'Klik atau drop untuk ganti',
  compact = false,
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const isAcceptedFile = validateFile || ((next) => /\.(jpe?g|png)$/i.test(next.name));

  const pickFile = (next) => {
    if (!next) {
      onFileChange(null);
      return;
    }
    if (!isAcceptedFile(next)) return;
    onFileChange(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) pickFile(dropped);
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const localPreview = file ? URL.createObjectURL(file) : null;
  const showPreview = localPreview || previewUrl;

  const zoneClass = [
    'relative cursor-pointer rounded-md border border-dashed text-left transition-colors duration-tesla',
    disabled ? 'cursor-not-allowed opacity-60' : '',
    dragging
      ? 'border-accent-brand bg-uniper-orange/10'
      : 'border-border-strong bg-surface-soft hover:border-accent-brand hover:bg-canvas dark:border-white/15 dark:bg-white/5 dark:hover:border-accent-brand',
  ].join(' ');

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="hidden"
      disabled={disabled}
      onChange={(e) => pickFile(e.target.files?.[0] || null)}
    />
  );

  const clearButton = file ? (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        pickFile(null);
        if (inputRef.current) inputRef.current.value = '';
      }}
      className="shrink-0 rounded-xs p-1.5 text-muted hover:bg-surface-soft hover:text-ink dark:hover:bg-white/10 dark:hover:text-white"
      aria-label="Hapus file"
    >
      <FaTimes className="text-xs" />
    </button>
  ) : null;

  if (compact) {
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onClick={openPicker}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={onDrop}
        className={`flex items-center gap-3 px-3 py-2.5 ${zoneClass}`}
      >
        {input}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xs border border-hairline bg-canvas dark:border-white/10 dark:bg-tile-1">
          {showPreview ? (
            <img src={showPreview} alt="Preview" className="h-full w-full object-contain" />
          ) : (
            <FaCloudUploadAlt className="text-base text-accent-brand" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {file ? (
            <>
              <p className="truncate text-sm font-medium text-ink dark:text-white">{file.name}</p>
              <p className="text-xs text-muted">
                {(file.size / 1024).toFixed(0)} KB · {replaceHint}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-ink dark:text-white">
                {emptyTitle}{' '}
                <span className="font-medium text-accent-brand">{pickLabel}</span>
              </p>
              <p className="text-xs text-muted">{hint}</p>
            </>
          )}
        </div>
        {clearButton}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onClick={openPicker}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={onDrop}
        className={`flex min-h-[112px] flex-col items-center justify-center gap-2 px-4 py-5 text-center ${zoneClass}`}
      >
        {input}

        {showPreview ? (
          <div className="flex w-full flex-col items-center gap-2">
            <img
              src={showPreview}
              alt="Preview"
              className="max-h-16 w-auto max-w-full object-contain"
            />
            <p className="max-w-full truncate text-xs text-muted">
              {file ? file.name : replaceHint}
            </p>
          </div>
        ) : (
          <>
            <FaCloudUploadAlt className="text-xl text-accent-brand" />
            <div>
              <p className="text-sm font-medium text-ink dark:text-white">
                {emptyTitle}{' '}
                <span className="text-accent-brand">{pickLabel}</span>
              </p>
              <p className="mt-0.5 text-xs text-muted">{hint}</p>
            </div>
          </>
        )}
      </div>

      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-canvas px-3 py-1.5 dark:border-white/10 dark:bg-tile-1">
          <p className="min-w-0 truncate text-xs text-body dark:text-white/80">
            {file.name}
            <span className="ml-2 text-muted">{(file.size / 1024).toFixed(0)} KB</span>
          </p>
          {clearButton}
        </div>
      ) : null}
    </div>
  );
};

export default ImageDropzone;
